// Shared stat card for both the global and per-tenant Analytics surfaces.
// Renders a label, a numeric value, optional sub-caption, and an optional
// delta-vs-prior-period line ("+18% vs prior 30d"). When `onSelect` is given
// the card renders as a button and doubles as the trend chart's metric
// selector — same interaction as the owner Analytics page.

interface AnalyticsStatCardProps {
  label: string
  value: number
  /** Equivalent value from the immediately-preceding equal-length period. */
  prev?: number | undefined
  /** Range span in days, used in the delta caption ("vs prior 30d"). */
  prevSpanDays?: number | undefined
  /** Static sub-line (rendered instead of delta when delta isn't applicable). */
  sub?: string | undefined
  /** When set, the card is a button that selects its metric for the chart. */
  onSelect?: () => void
  selected?: boolean
}

export function AnalyticsStatCard({
  label,
  value,
  prev,
  prevSpanDays,
  sub,
  onSelect,
  selected,
}: AnalyticsStatCardProps) {
  const delta = formatDelta(value, prev, prevSpanDays)
  const body = (
    <>
      <span className="admin-stat-label">{label}</span>
      <span className="admin-stat-value">{value.toLocaleString()}</span>
      {delta ? (
        <span className={`admin-stat-sub admin-stat-delta-${delta.tone}`}>
          {delta.text}
        </span>
      ) : sub ? (
        <span className="admin-stat-sub">{sub}</span>
      ) : null}
    </>
  )

  if (!onSelect) {
    return <div className="admin-stat-card">{body}</div>
  }
  return (
    <button
      type="button"
      className={`admin-stat-card admin-stat-card-selectable${
        selected ? ' is-selected' : ''
      }`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {body}
    </button>
  )
}

type DeltaTone = 'up' | 'down' | 'flat' | 'new'

interface FormattedDelta {
  text: string
  tone: DeltaTone
}

export function formatDelta(
  value: number,
  prev: number | undefined,
  prevSpanDays: number | undefined,
): FormattedDelta | null {
  if (prev === undefined || prevSpanDays === undefined) return null
  const suffix = `vs prior ${prevSpanDays}d`
  if (prev === 0 && value === 0) {
    return { text: `— ${suffix}`, tone: 'flat' }
  }
  if (prev === 0 && value > 0) {
    return { text: `new ${suffix}`, tone: 'new' }
  }
  const pct = ((value - prev) / prev) * 100
  if (Math.abs(pct) < 0.05) {
    return { text: `±0% ${suffix}`, tone: 'flat' }
  }
  const rounded = pct >= 10 || pct <= -10 ? Math.round(pct) : Math.round(pct * 10) / 10
  const sign = pct > 0 ? '+' : ''
  const tone: DeltaTone = pct > 0 ? 'up' : 'down'
  return { text: `${sign}${rounded}% ${suffix}`, tone }
}
